import { useEffect, useRef, useState } from 'react';

import { pointAtDistance, projectToPath } from './pathGeometry';

/** Used for the first tween, before two fixes have been seen to measure with. */
const DEFAULT_GAP_MS = 2500;
/**
 * The tween runs slightly longer than the gap it is covering, so the car is
 * still moving when the next fix lands. With duration == gap exactly, every
 * cycle ends a few frames early — the scheduler is never that precise — and
 * those gaps are the stop-go.
 */
const GLIDE_OVERLAP = 1.15;
/** Clamp: a burst of fixes should not make the car crawl, or a long gap sprint. */
const MIN_GAP_MS = 250;
const MAX_GAP_MS = 8000;
/**
 * How many recent gaps the cadence is taken from.
 *
 * The MEDIAN of these, not the last one: a single long gap — the app
 * backgrounded, a tunnel, the sim paused — would otherwise size the next tween
 * to it, and the car would crawl through the following fix at a fraction of
 * its real speed. A median ignores one outlier and still tracks a feed that
 * has genuinely changed pace.
 */
const GAP_SAMPLES = 5;

const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
};
/** Beyond this jump (a GPS correction, a reconnect) snap instead of gliding. */
export const GLIDE_SNAP_METERS = 500;
/**
 * How far off the route a fix can be and still be drawn on it.
 *
 * Snapping is what keeps the car on the road instead of cutting the corner of
 * every bend, but past this the driver has actually left the route — a wrong
 * turn, a diversion — and an ops tool has to show that rather than hide it.
 */
export const ROUTE_SNAP_METERS = 60;

const EARTH_RADIUS_M = 6371000;
const toRad = (deg) => (deg * Math.PI) / 180;

/** Great-circle distance in metres. */
export const distanceMeters = (from, to) => {
  const dLat = toRad(to.lat - from.lat);
  const dLng = toRad(to.lng - from.lng);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
};

/**
 * Interpolates a heading the short way round, so 350° → 10° turns 20° through
 * north rather than 340° back through south — the difference between a car
 * nudging right and one spinning on the spot.
 */
export const interpolateBearing = (from, to, t) => {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  const delta = ((((to - from) % 360) + 540) % 360) - 180;
  return (((from + delta * clamped) % 360) + 360) % 360;
};

/** Linear interpolation between two coordinates at fraction `t` (0..1). */
export const interpolate = (from, to, t) => {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  return {
    lat: from.lat + (to.lat - from.lat) * clamped,
    lng: from.lng + (to.lng - from.lng) * clamped,
  };
};

/** Whether a move is too large to glide through — snap instead. */
export const shouldSnap = (from, to, thresholdMeters = GLIDE_SNAP_METERS) => distanceMeters(from, to) > thresholdMeters;

/**
 * Glides a marker toward each new fix instead of teleporting it, turning it as
 * it goes.
 *
 * A port of hitch-rn's `useDriverGlide` (rider-trips/hooks), down to the
 * constants: same 2.5s tween against the same ~2.5s ping cadence, same 500m
 * snap threshold. This page exists to show what the rider sees, and a car that
 * jumps every few seconds does not look like the one in their app.
 *
 * Bearing is tweened alongside position so a sprite-based car steps through
 * its frames as it turns, rather than snapping to the new heading the instant
 * a ping lands. A new target restarts the tween from wherever the marker
 * currently is, so back-to-back pings do not fight each other.
 *
 * @param  {{lat: number, lng: number, bearing?: number}|null} target - The
 *   newest fix
 * @returns {{lat: number, lng: number, bearing: number}|null} - Where to draw
 *   the car this frame
 */
export const useDriverGlide = (target, route = null) => {
  // Seeded null, not from `target`: the first fix has to go through the effect
  // to be snapped onto the route, and seeding the raw fix here drew the car
  // beside the road on the first frame and then slid it on.
  const [position, setPosition] = useState(null);
  const current = useRef(null);
  const lastFixAt = useRef(null);
  const gaps = useRef([]);

  const lat = target?.lat;
  const lng = target?.lng;
  const bearing = target?.bearing;

  useEffect(() => {
    if (lat === undefined || lng === undefined) return undefined;

    // On the route where the fix is close enough to it: tween the distance
    // ALONG the path, not a straight line between fixes, so the car follows
    // bends instead of cutting across them.
    const projected = route ? projectToPath(route, { lat, lng }) : null;
    const onRoute = projected !== null && projected.offsetMeters <= ROUTE_SNAP_METERS;

    const to = onRoute
      ? { ...pointAtDistance(route, projected.along), along: projected.along }
      : { lat, lng, bearing: bearing ?? current.current?.bearing ?? 0 };
    const from = current.current;

    // Measure the cadence rather than assuming it: the sim runs at 4x, a real
    // driver's pings stretch in bad signal, and a tween sized for 2.5s either
    // finishes early (stop-go) or never arrives.
    const now = Date.now();
    if (lastFixAt.current) {
      const measured = Math.min(MAX_GAP_MS, Math.max(MIN_GAP_MS, now - lastFixAt.current));
      gaps.current = [...gaps.current, measured].slice(-GAP_SAMPLES);
    }
    lastFixAt.current = now;
    const gap = gaps.current.length ? median(gaps.current) : DEFAULT_GAP_MS;

    if (!from || shouldSnap(from, to)) {
      current.current = to;
      setPosition(to);
      return undefined;
    }

    const duration = gap * GLIDE_OVERLAP;
    const startTime = now;
    let frame = null;
    // Both ends on the route: ride it. The car may have been tweening in
    // straight-line mode when the route arrived — or be mid-tween, since a
    // tween outlasts its gap by design and never sets `along` on the frame it
    // is interrupted at — so where it currently is gets projected rather than
    // waiting for an `along` that will not come.
    const alongFrom = onRoute ? (from.along ?? projectToPath(route, { lat: from.lat, lng: from.lng }).along) : null;

    // requestAnimationFrame, not a 50ms timer: the browser paints on its own
    // rhythm, and a timer that lands between paints spends its motion on
    // frames nobody sees — which reads as judder, most visibly when zoomed out
    // where each step is a couple of pixels.
    const tick = () => {
      const t = (Date.now() - startTime) / duration;
      if (t >= 1) {
        current.current = to;
        setPosition(to);
        return;
      }
      const next =
        alongFrom === null
          ? {
              ...interpolate(from, to, t),
              bearing: interpolateBearing(from.bearing ?? 0, to.bearing, t),
            }
          : {
              ...pointAtDistance(route, alongFrom + (to.along - alongFrom) * t),
              along: alongFrom + (to.along - alongFrom) * t,
            };
      current.current = next;
      setPosition(next);
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
    };
    // Fixes only, not `route`: a refreshed polyline arrives as a new object
    // identity and would restart the tween mid-glide. The next fix picks the
    // new route up, which is at most a couple of seconds away.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, bearing]);

  return position;
};
